import { useCallback } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import { TooltipLink } from "@/v2-backoffice-client/components/link"
import Page, { type PageProps } from "@/v2-backoffice-client/components/page"
import UptimeBoard from "@/v2-backoffice-client/uptime/UptimeBoard"
import UptimeDashboardView from "@/v2-backoffice-client/uptime/dashboard/UptimeDashboardView"
import UptimeConfigSheet from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigSheet"
import { useUptimeStatusConfigBootstrap } from "@/v2-backoffice-client/uptime/uptime.statusConfigBootstrap"
import { useSearchParamOpener } from "@/v2-backoffice-client/utils"
import appConfig from "@/v2-backoffice-client/config"

export type UptimePageProps = PageProps

// `?display=1` toggles into the wall-display dashboard mode.
// `?config=1` opens the editorial Configuration sheet alongside
// the board so the operator can edit platforms / categories /
// providers / probes without leaving the page. Both are exposed
// as openers so the Exit button + ESC handler can call `close()`
// and the URLs remain bookmarkable for kiosk and deep-link uses.
const DISPLAY_PARAM = "display"
const CONFIG_PARAM = "config"

function UptimePage(props: UptimePageProps) {
  const { className, ...pageProps } = props
  const display = useSearchParamOpener(DISPLAY_PARAM)
  const config = useSearchParamOpener(CONFIG_PARAM)

  // Populate the module-level status-config cache the synchronous
  // helpers in `uptime.utils.ts` read from. Cheap to call (Apollo
  // dedupes the request) and ensures every uptime surface sees
  // the operator-edited labels / providers.
  useUptimeStatusConfigBootstrap()

  const handleEnter = useCallback(() => display.open("1"), [display])
  const handleExit = useCallback(() => display.close(), [display])
  const handleConfigOpen = useCallback(() => config.open("1"), [config])
  const handleConfigClose = useCallback(() => config.close(), [config])

  if (display.isActive) {
    return <UptimeDashboardView onExit={handleExit} />
  }

  return (
    <Page className={cn("uptime-page", className)} {...pageProps}>
      <Page.Header>
        <Page.Title>Uptime</Page.Title>
        <Page.Header.Actions>
          <TooltipLink
            to={appConfig.statusPageUrl}
            content="Open in new tab"
            variant="ghost"
            iconSrc="link"
            className="uptime-display-toggle"
          >
            Status Page
          </TooltipLink>

          <button
            type="button"
            className="uptime-display-toggle"
            title="Edit the editorial config that drives the public status page"
            onClick={handleConfigOpen}
          >
            <Icon src="settings" />
            Configuration
          </button>

          <button
            type="button"
            className="uptime-display-toggle"
            title="Open the wall-display incident dashboard"
            onClick={handleEnter}
          >
            <Icon src="expand" />
            Display mode
          </button>
        </Page.Header.Actions>
      </Page.Header>

      <Page.Content className="flex-1 min-h-0 overflow-auto py-2 gap-4">
        <UptimeBoard />
      </Page.Content>

      <UptimeConfigSheet
        open={config.isActive}
        onOpenChange={(o) => {
          if (!o) handleConfigClose()
        }}
      />
    </Page>
  )
}

export default UptimePage
